import React, { useState } from 'react';
import PropTypes from 'prop-types';

import {
  SR_API_ACTION_ARCHIVE_RENTLINX_LISTING_LEAD,
  SR_API_ACTION_UNARCHIVE_RENTLINX_LISTING_LEAD,
} from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_RENTLINX_LEADS,
  DATA_TABLE_VIEW_ID_RENLINX_LEADS_ACTIVE,
  DATA_TABLE_VIEW_ID_RENLINX_LEADS_ARCHIVED,
} from 'common/dataTableConstants';

import extractPhoneFromText from 'common/util/extractPhoneFromText';
import setNullEmptyValues from 'common/util/setNullEmptyValues';

import postWithState from 'client/actions/postWithState';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import SrRentlinxLeadNotesModal from 'client/components/SrRentlinxLeadNotesModal';
import SrHouseholdKanbanBoardCardV2 from 'client/components/SrHouseholdKanbanBoardCardV2/SrHouseholdKanbanBoardCardV2';
import SrArchiveListingLeadModal from 'client/components/SrArchiveListingLeadModal';

import RentlinxLeadActions from 'client/actions/RentlinxLead';

export const getDataTableSettingsFromProps = ({
  moveInDateRanges,
  listingManagementCompany,
  listingServices,
  accountManagerUserId,
  onlyActive,
  itemsPropName,
  count = 20,
  filter = {},
}) => {
  const dataTableSettings = {
    itemsPropName,
    rootViewId: onlyActive
      ? DATA_TABLE_VIEW_ID_RENLINX_LEADS_ACTIVE
      : DATA_TABLE_VIEW_ID_RENLINX_LEADS_ARCHIVED,
    dataSource: DATA_TABLE_SOURCE_RENTLINX_LEADS,
    count,
    initialViewSettings: {
      includeItemsCount: true,
      filter,
      customParams: {
        householdKanbanRentlinxLeads: {
          createdDateRanges: moveInDateRanges,
          listingManagementCompany,
          listingServices,
          accountManagerUserId,
          onlyActive: onlyActive,
        },
      },
    },
  };

  return dataTableSettings;
};

const SrHouseholdKanbanBoardSwimlaneRentlinxLeads = (props) => {
  const { currentUser, dispatch } = props;
  const [searchTerm, onSearchTermChange] = useState('');
  const [rentlixLeadIdToNotes, setRentlixLeadIdToNotes] = useState(null);
  const activeDataTableSettings = getDataTableSettingsFromProps({
    ...props,
    onlyActive: true,
    itemsPropName: 'activeLeads',
  });
  const archivedDataTableSettings = getDataTableSettingsFromProps({
    ...props,
    onlyActive: false,
    itemsPropName: 'archivedLeads',
  });
  const [archiveRentlinxListingLeadId, setArchiveRentlixListingLeadId] = useState(null);
  const [showArchivedLeads, setShowArchivedLeads] = useState(false);

  const onClickArchive = (rentlinxListingLeadId) => {
    setArchiveRentlixListingLeadId(rentlinxListingLeadId);
  };

  const onToggleArchivedLeads = () => {
    setShowArchivedLeads(!showArchivedLeads);
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const rentlinxLead = await dispatch(RentlinxLeadActions.getByPhone, {
      phone: Number(extractPhoneFromText(searchTerm)),
    });

    if (!rentlinxLead) {
      return global.alert(`Lead with phone "${searchTerm}" not found.`);
    }

    props.history.push(`/admin/lead/${rentlinxLead.id}`);
  };

  return (
    <div className="householdKanbanBoardColumn">
      <SrDataTableConnected {...activeDataTableSettings}>
        {({
          activeLeads,
          activeLeadsCount,
          dataTableApiState,
          reloadItems,
          onInfiniteScrollWrapperRef,
        }) => {
          const onSubmitArchiveListing = (values) => {
            dispatch(postWithState, {
              action: SR_API_ACTION_ARCHIVE_RENTLINX_LISTING_LEAD,
              payload: setNullEmptyValues({
                rentlinxListingLeadId: archiveRentlinxListingLeadId,
                archiveReasons: values.archiveReasons,
                archiveReasonsDescription: values.archiveReasonsDescription,
                partnerArchiveReason: values.partnerArchiveReason,
              }),
            }).then(() => {
              setArchiveRentlixListingLeadId(null);
              const archiveRentlinxLead = activeLeads.find(({ listings }) => {
                return listings.some(({ rentlinxListingLeadId }) => {
                  return rentlinxListingLeadId === archiveRentlinxListingLeadId;
                });
              });
              return reloadItems({
                count: 1,
                filter: {
                  'rentlinxLeads.id': archiveRentlinxLead?.id,
                },
                customParams: activeDataTableSettings.customParams,
              });
            });
          };

          return (
            <>
              <div className="columnName">
                {`leads${typeof activeLeadsCount === 'number' ? ` (${activeLeadsCount})` : ''}`}
                <form method="post" onSubmit={onSubmit} className="searchContainer">
                  <input
                    value={searchTerm}
                    placeholder="search"
                    onChange={(event) => onSearchTermChange(event.target.value)}
                  />
                  <button type="submit" />
                </form>
              </div>
              <div className="columnCardContainer leads">
                <div className="leadsInfiniteScrollWrapper" ref={onInfiniteScrollWrapperRef}>
                  {activeLeads.map((lead) => {
                    return (
                      <SrHouseholdKanbanBoardCardV2
                        key={lead.id}
                        id={lead.id}
                        name={lead.name}
                        phone={lead.phone}
                        email={lead.email}
                        note={lead.lastNote}
                        frontConversation={lead.frontConversation}
                        listings={lead.listings}
                        onClickNote={setRentlixLeadIdToNotes}
                        onClickArchive={onClickArchive}
                      />
                    );
                  })}
                </div>
                <SrDataTableConnected {...archivedDataTableSettings}>
                  {({
                    archivedLeads,
                    archivedLeadsCount,
                    dataTableApiState,
                    reloadItems,
                    onInfiniteScrollWrapperRef,
                  }) => {
                    const onClickUnarchive = (rentlinxListingLeadId) => {
                      let listing;
                      const unarchiveRentlinxLead = archivedLeads.find(({ listings }) => {
                        return listings.some((_listing) => {
                          const isMatch = _listing.rentlinxListingLeadId === rentlinxListingLeadId;
                          if (isMatch) {
                            listing = _listing;
                          }
                          return isMatch;
                        });
                      });

                      if (
                        !global.confirm(
                          `Are you sure you want to un-archive ${listing.addressStreetDisplay} ` +
                            `for ${unarchiveRentlinxLead.name}?`,
                        )
                      ) {
                        return;
                      }
                      dispatch(postWithState, {
                        action: SR_API_ACTION_UNARCHIVE_RENTLINX_LISTING_LEAD,
                        payload: {
                          rentlinxListingLeadId: listing?.rentlinxListingLeadId,
                        },
                      }).then(() => {
                        return reloadItems({
                          count: 1,
                          filter: {
                            'rentlinxLeads.id': unarchiveRentlinxLead?.id,
                          },
                        });
                      });
                    };

                    return (
                      <div ref={onInfiniteScrollWrapperRef}>
                        {typeof archivedLeadsCount === 'number' && archivedLeadsCount > 0 && (
                          <>
                            <div className="archivedText" onClick={onToggleArchivedLeads}>
                              {showArchivedLeads
                                ? 'archived'
                                : `View ${archivedLeadsCount} archived`}
                            </div>
                            {showArchivedLeads &&
                              archivedLeads.map((lead) => {
                                return (
                                  <SrHouseholdKanbanBoardCardV2
                                    id={lead.id}
                                    key={lead.id}
                                    name={lead.name}
                                    phone={lead.phone}
                                    email={lead.email}
                                    note={lead.lastNote}
                                    frontConversation={lead.frontConversation}
                                    listings={lead.listings}
                                    onClickNote={setRentlixLeadIdToNotes}
                                    onClickUnarchive={onClickUnarchive}
                                    onClickArchive={onClickArchive}
                                  />
                                );
                              })}
                            {dataTableApiState.inProgress && <div className="loading-ellipses" />}
                          </>
                        )}
                      </div>
                    );
                  }}
                </SrDataTableConnected>
              </div>
              {dataTableApiState.inProgress && <div className="loading-ellipses" />}
              {rentlixLeadIdToNotes && (
                <SrRentlinxLeadNotesModal
                  rentlinxLeadId={rentlixLeadIdToNotes}
                  dispatch={dispatch}
                  currentUser={currentUser}
                  onClose={() => setRentlixLeadIdToNotes(null)}
                  onUpdate={() =>
                    reloadItems({
                      count: 1,
                      customParams: activeDataTableSettings.initialViewSettings.customParams,
                      filter: { 'rentlinxLeads.id': rentlixLeadIdToNotes },
                    })
                  }
                />
              )}
              {archiveRentlinxListingLeadId && (
                <SrArchiveListingLeadModal
                  onClose={() => setArchiveRentlixListingLeadId(null)}
                  onSubmit={(values) => {
                    onSubmitArchiveListing(values);
                    setArchiveRentlixListingLeadId(null);
                  }}
                />
              )}
            </>
          );
        }}
      </SrDataTableConnected>
    </div>
  );
};

SrHouseholdKanbanBoardSwimlaneRentlinxLeads.propTypes = {
  currentUser: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
};

SrHouseholdKanbanBoardSwimlaneRentlinxLeads.displayName =
  'SrHouseholdKanbanBoardSwimlaneRentlinxLeads';

export default SrHouseholdKanbanBoardSwimlaneRentlinxLeads;
